import styles from './StoriesCarousel.module.css';
import { SectionWrapper } from '../shared/SectionWrapper';
import { Chip } from '../shared/Chip';
import { ScriptText } from '../shared/ScriptText';
import { useStories } from '../../hooks/useStories';
import type { StoryMeta } from '../../hooks/useStories';

const modeColors: Record<string, string> = {
  soft: '#F472B6',
  steamy: '#EC4899',
  wild: '#DB2777',
  hardcore: '#9D174D',
};

function StoryCard({
  story: s,
  displayIndex,
  loading,
}: {
  story: StoryMeta;
  displayIndex: number;
  loading: boolean;
}) {
  const color = modeColors[s.storyMode] ?? '#FF2E7E';
  return (
    <div className={styles.card}>
      {s.imageSrc ? <img src={s.imageSrc} alt={s.title} className={styles.cardImg} /> : null}
      <div className={styles.cardOverlay} />
      <div className={styles.cardContent}>
        <span className={styles.number}>{String(displayIndex + 1).padStart(2, '0')}</span>
        <div className={styles.modeBadge} style={{ color, borderColor: `${color}55` }}>
          {s.storyMode}
        </div>
        <h3 className={styles.storyTitle}>{s.title}</h3>
        <p className={styles.storySub}>{s.description?.replace(/\$p1|\$p2/g, '___')}</p>
      </div>
      {loading && <div className={styles.shimmer} />}
    </div>
  );
}

export function StoriesCarousel() {
  const { stories, loading } = useStories();
  const n = stories.length;
  const looped = n > 0 ? [...stories, ...stories] : [];
  const durationSec = Math.max(n * 14, 48);

  return (
    <SectionWrapper id="stories" className={styles.section}>
      <div className={styles.header}>
        <Chip>Stories for Two</Chip>
        <h2 className={`display ${styles.title}`}>
          Read together. Play <ScriptText rotate={-2}>together.</ScriptText>
        </h2>
      </div>

      <div className={styles.trackWrap}>
        <div className={styles.trackDesktop}>
          {stories.map((s, i) => (
            <StoryCard key={s.id ?? i} story={s} displayIndex={i} loading={loading} />
          ))}
        </div>

        <div
          className={styles.trackMobile}
          style={{ ['--marquee-duration' as string]: `${durationSec}s` }}
        >
          <div className={styles.marqueeRow}>
            {looped.map((s, i) => (
              <StoryCard
                key={`${s.id ?? 's'}-marq-${i}`}
                story={s}
                displayIndex={n ? i % n : 0}
                loading={loading}
              />
            ))}
          </div>
        </div>
      </div>
    </SectionWrapper>
  );
}
